import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Lock, LogOut, Download, Loader2, ShieldCheck, Search, Waves } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sinaletas_admin_token";

function formatApiError(detail) {
  if (!detail) return "Algo salió mal. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  }
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loadingAuth, setLoadingAuth] = useState(true);
  const navigate = useNavigate();

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setLoadingAuth(false);
      return;
    }
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setLoadingAuth(false))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setLoadingAuth(false);
      });
  }, [token]);

  const handleLogin = (newToken) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    toast.success("Sesión cerrada");
  };

  if (loadingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white font-body">
      {token ? (
        <Dashboard token={token} onLogout={handleLogout} onBack={() => navigate("/")} />
      ) : (
        <LoginScreen onLogin={handleLogin} onBack={() => navigate("/")} />
      )}
    </div>
  );
}

function LoginScreen({ onLogin, onBack }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      toast.success("Bienvenida 👋");
      onLogin(data.token);
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || "Error de autenticación";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-ocean-gradient grain">
      <div className="w-full max-w-md">
        <button
          onClick={onBack}
          data-testid="admin-back-home"
          className="text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-cyan-300 mb-6 flex items-center gap-2 transition"
        >
          <Waves className="w-3.5 h-3.5" /> Volver al sitio
        </button>

        <div
          data-testid="admin-login-card"
          className="bg-slate-900/60 border border-white/10 rounded-3xl p-8 sm:p-10 backdrop-blur-xl shadow-2xl"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center">
              <Lock className="w-4.5 h-4.5 text-cyan-400" />
            </div>
            <span className="text-xs uppercase tracking-[0.2em] font-medium text-cyan-400">
              Panel privado
            </span>
          </div>

          <h1 className="font-heading text-3xl font-bold mb-2 leading-tight">
            Acceso administradora
          </h1>
          <p className="text-sm text-slate-400 mb-8">
            Introduce la contraseña para ver y exportar los registros recogidos.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] font-medium text-slate-400 mb-2">
                Contraseña
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                required
                disabled={submitting}
                data-testid="admin-password-input"
                placeholder="••••••••"
                className="w-full bg-slate-950/60 border border-slate-700 focus:border-cyan-400 rounded-full px-6 py-3.5 text-white outline-none transition-all placeholder:text-slate-600 disabled:opacity-50"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-button"
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold px-6 py-3.5 rounded-full transition-all duration-300 shadow-[0_0_20px_rgba(34,211,238,0.3)] hover:shadow-[0_0_30px_rgba(34,211,238,0.5)] hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verificando…
                </>
              ) : (
                <>Entrar</>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout, onBack }) {
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/registrations`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        setTotal(data.total);
        setItems(data.items);
      })
      .catch(() => toast.error("No se pudo cargar la lista"))
      .finally(() => setLoading(false));
  }, [token]);

  const filtered = items.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      r.name?.toLowerCase().includes(q) ||
      r.email?.toLowerCase().includes(q)
    );
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/registrations/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().split("T")[0];
      link.setAttribute("download", `sinaletas-registros-${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch {
      toast.error("No se pudo exportar");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ocean-gradient">
      {/* Header */}
      <header className="border-b border-white/10 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center">
              <ShieldCheck className="w-4.5 h-4.5 text-cyan-400" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-medium text-cyan-400">
                Panel admin
              </div>
              <div className="font-heading font-bold text-sm">
                Sin Aletas No Hay Paraíso
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="text-sm text-slate-400 hover:text-cyan-300 transition hidden sm:inline-block"
              data-testid="admin-go-home"
            >
              Ver sitio
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 border border-white/10 text-slate-200 text-sm font-medium px-4 py-2 rounded-full transition"
            >
              <LogOut className="w-3.5 h-3.5" />
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-8 py-10 sm:py-14">
        {/* Stats + Actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6 mb-10">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
              <span className="text-cyan-400">{total}</span>{" "}
              <span className="text-slate-300 font-medium">
                {total === 1 ? "registro" : "registros"}
              </span>
            </h1>
            <p className="text-slate-400 mt-2 text-sm">
              Personas suscritas al pre-lanzamiento.
            </p>
          </div>

          <button
            onClick={handleExport}
            disabled={exporting || !items.length}
            data-testid="admin-export-csv-button"
            className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-sm px-5 py-3 rounded-full transition-all duration-300 shadow-[0_0_20px_rgba(34,211,238,0.3)] hover:shadow-[0_0_30px_rgba(34,211,238,0.5)] hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Exportar CSV
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o email…"
            data-testid="admin-search-input"
            className="w-full bg-slate-900/60 border border-slate-800 focus:border-cyan-400 rounded-full pl-12 pr-6 py-3 text-sm text-white outline-none transition-all placeholder:text-slate-500"
          />
        </div>

        {/* Table */}
        <div
          data-testid="admin-table-wrapper"
          className="bg-slate-900/40 border border-white/5 rounded-2xl overflow-hidden backdrop-blur-sm"
        >
          {loading ? (
            <div className="py-20 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center text-slate-500 text-sm">
              {items.length === 0
                ? "Aún no hay registros."
                : "No hay coincidencias para tu búsqueda."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-registrations-table">
                <thead>
                  <tr className="text-xs uppercase tracking-[0.15em] text-slate-500 border-b border-white/5">
                    <th className="text-left font-medium px-6 py-4">Nombre</th>
                    <th className="text-left font-medium px-6 py-4">Email</th>
                    <th className="text-left font-medium px-6 py-4">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`admin-row-${r.id}`}
                      className="border-b border-white/5 last:border-0 hover:bg-slate-800/30 transition"
                    >
                      <td className="px-6 py-4 text-white font-medium">{r.name}</td>
                      <td className="px-6 py-4 text-slate-300">
                        <a
                          href={`mailto:${r.email}`}
                          className="hover:text-cyan-400 transition"
                        >
                          {r.email}
                        </a>
                      </td>
                      <td className="px-6 py-4 text-slate-500 whitespace-nowrap">
                        {formatDate(r.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {filtered.length > 0 && (
          <p className="text-xs text-slate-500 mt-4 text-right">
            Mostrando {filtered.length} de {total}
          </p>
        )}
      </main>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
